import { getShotZone, shotSpot } from '@/utils/shotChart'
import { SHOT_DETAIL_GROUPS } from '@/utils/shotDetails'

// Advanced analytics of a played match, from what the match page already has: the box score of every player
// (match.team1/team2.players[].stats[0]) and the play-by-play (logs). Possession based metrics follow the usual
// definitions (Dean Oliver's four factors, possessions = FGA - ORB + TOV + 0.44 x FTA).

const REGULATION_SECONDS = 40 * 60
const OVERTIME_SECONDS = 5 * 60
const CLUTCH_SECONDS = 5 * 60
const CLUTCH_MARGIN = 5
// a lineup has to have played this long to be listed
const MIN_LINEUP_SECONDS = 120
// the share of the plays that must have had 5 on court for each team for +/- and the lineups to be shown:
// a missed substitution now and then (4 or 6 on court for a while) does not throw the rest away
const MIN_TRACKED_SHARE = 0.9

const num = value => Number(value) || 0
const ratio = (a, b) => (b ? a / b : null)
const pct = (a, b) => (b ? (a / b) * 100 : null)

function logPoints(log) {
    return num(log.twoPointsMade) * 2 + num(log.threePointsMade) * 3 + num(log.freeThrowsMade)
}

function isFieldGoal(log) {
    return num(log.twoPointAttempts) > 0 || num(log.threePointAttempts) > 0 || num(log.twoPointsMade) > 0 || num(log.threePointsMade) > 0
}

function shotValue(log) {
    return num(log.threePointAttempts) > 0 || num(log.threePointsMade) > 0 ? 3 : 2
}

function isMade(log) {
    return num(log.twoPointsMade) > 0 || num(log.threePointsMade) > 0
}

// "25:22" (game time elapsed) -> seconds
function elapsedSeconds(log) {
    const [minutes, seconds] = String(log.timestamp || '0:0').split(':').map(Number)
    return (Number.isFinite(minutes) ? minutes : 0) * 60 + (Number.isFinite(seconds) ? seconds : 0)
}

// the period of a play: as logged, else by its game time (4 periods of 10 minutes, then overtimes of 5),
// for the matches whose play-by-play was logged without periods
function periodOf(log, at) {
    const period = num(log.period)
    if (period >= 1) return period
    if (at <= REGULATION_SECONDS) return Math.max(1, Math.ceil(at / 600))
    return 4 + Math.ceil((at - REGULATION_SECONDS) / OVERTIME_SECONDS)
}

function orderedLogs(logs) {
    return (Array.isArray(logs) ? logs : [])
        .filter(log => log && !log.matchEnded)
        .slice()
        .sort((a, b) => (num(a.createdAt) - num(b.createdAt)) || String(a.logId || '').localeCompare(String(b.logId || '')))
}

function emptyTotals() {
    return { pts: 0, fgm2: 0, fga2: 0, fgm3: 0, fga3: 0, ftm: 0, fta: 0, orb: 0, drb: 0, ast: 0, stl: 0, blk: 0, tov: 0, pf: 0 }
}

function addStats(totals, stats) {
    totals.fgm2 += num(stats.twoPointsMade)
    totals.fga2 += num(stats.twoPointAttempts)
    totals.fgm3 += num(stats.threePointsMade)
    totals.fga3 += num(stats.threePointAttempts)
    totals.ftm += num(stats.freeThrowsMade)
    totals.fta += num(stats.freeThrowAttempts)
    totals.orb += num(stats.offensiveRebounds)
    totals.drb += num(stats.defensiveRebounds)
    totals.ast += num(stats.assists)
    totals.stl += num(stats.steals)
    totals.blk += num(stats.blockedShots)
    totals.tov += num(stats.turnovers)
    totals.pf += num(stats.personalFouls)
    totals.pts = totals.fgm2 * 2 + totals.fgm3 * 3 + totals.ftm
    return totals
}

function withDerived(totals) {
    const fgm = totals.fgm2 + totals.fgm3
    const fga = totals.fga2 + totals.fga3
    return { ...totals, fgm, fga }
}

// Team totals from the play-by-play (it has the team rebounds / turnovers too), or the players' box score
function teamTotals(team, logs) {
    const teamLogs = logs.filter(log => String(log.teamId) === String(team.id) && !log.playerMatchStatus)
    if (teamLogs.length) {
        return withDerived(teamLogs.reduce((totals, log) => addStats(totals, log), emptyTotals()))
    }
    const players = Array.isArray(team.players) ? team.players : []
    return withDerived(players.reduce((totals, player) => addStats(totals, (player.stats && player.stats[0]) || {}), emptyTotals()))
}

function possessions(t) {
    return t.fga - t.orb + t.tov + 0.44 * t.fta
}

function playerLabel(player) {
    if (!player) return 'Ομάδα'
    return `${player.name || ''} ${player.surname || ''}`.trim() || `#${player.jersey}`
}

function playerShort(player) {
    if (!player) return 'Ομάδα'
    const surname = (player.surname || '').trim()
    const initial = (player.name || '').trim().charAt(0)
    return surname ? `${initial ? initial + '. ' : ''}${surname}` : playerLabel(player)
}

// ---------- Efficiency ----------
function efficiency(t1, t2, gameMinutes) {
    const poss1 = possessions(t1)
    const poss2 = possessions(t2)
    const poss = (poss1 + poss2) / 2
    const team = (t, o) => ({
        ortg: poss ? (t.pts / poss) * 100 : null,
        drtg: poss ? (o.pts / poss) * 100 : null,
        net: poss ? ((t.pts - o.pts) / poss) * 100 : null,
        ts: pct(t.pts, 2 * (t.fga + 0.44 * t.fta)),
        efg: pct(t.fgm + 0.5 * t.fgm3, t.fga),
        tov: pct(t.tov, t.fga + 0.44 * t.fta + t.tov),
        orb: pct(t.orb, t.orb + o.drb),
        drb: pct(t.drb, t.drb + o.orb),
        ftr: ratio(t.ftm, t.fga),
        astPct: pct(t.ast, t.fgm),
        astTo: ratio(t.ast, t.tov),
        pps: ratio(t.pts - t.ftm, t.fga)
    })
    return {
        possessions: poss,
        pace: gameMinutes ? (poss * 40) / gameMinutes : null,
        team1: team(t1, t2),
        team2: team(t2, t1)
    }
}

// ---------- Shot profile ----------
const PROFILE_FAMILIES = [
    { key: 'paint', label: 'Ρακέτα' },
    { key: 'mid', label: 'Μέση απόσταση' },
    { key: 'corner3', label: 'Τρίποντο γωνίας' },
    { key: 'above3', label: 'Τρίποντο υπόλοιπο' }
]

function profileFamily(log) {
    const spot = log.throwCoordinates
    if (!spot || !Number.isFinite(Number(spot.x)) || !Number.isFinite(Number(spot.y))) return null
    const zone = getShotZone({ ...shotSpot(spot), points: shotValue(log) })
    if (!zone) return null
    if (zone === 'paint') return 'paint'
    if (zone.startsWith('mid')) return 'mid'
    return zone.startsWith('corner') ? 'corner3' : 'above3'
}

function shotProfile(teamLogs) {
    const shots = teamLogs.filter(isFieldGoal)
    const located = shots.map(log => ({ log, family: profileFamily(log) })).filter(item => item.family)
    const total = located.length
    const rows = PROFILE_FAMILIES.map(family => {
        const items = located.filter(item => item.family === family.key)
        const made = items.filter(item => isMade(item.log)).length
        const points = items.reduce((sum, item) => sum + (isMade(item.log) ? shotValue(item.log) : 0), 0)
        return {
            key: family.key,
            label: family.label,
            attempts: items.length,
            made,
            points,
            share: pct(items.length, total),
            fgPct: pct(made, items.length),
            pps: ratio(points, items.length)
        }
    })
    return { total, rows }
}

// ---------- Where the points came from ----------
function startersOf(team, logs) {
    const starters = []
    for (const log of logs) {
        if (String(log.teamId) === String(team.id) && log.playerMatchStatus === 'begin' && !starters.includes(String(log.playerId))) {
            starters.push(String(log.playerId))
            if (starters.length === 5) break
        }
    }
    return starters
}

function pointSources(team, totals, teamLogs, logs, profile) {
    const players = Array.isArray(team.players) ? team.players : []
    const statOf = player => (player.stats && player.stats[0]) || {}
    const sum = key => players.reduce((total, player) => total + num(statOf(player)[key]), 0)
    const starters = startersOf(team, logs)
    const benchPoints = starters.length === 5
        ? players.filter(player => !starters.includes(String(player.id))).reduce((total, player) => total + num(statOf(player).points), 0)
        : null
    const fastbreak = teamLogs.filter(log => isFieldGoal(log) && isMade(log) && log.situation === 'FASTBREAK')
        .reduce((total, log) => total + shotValue(log), 0)
    const tagged = teamLogs.some(log => log.situation)
    const paint = profile.rows.find(row => row.key === 'paint')
    return {
        total: totals.pts,
        paint: paint ? paint.points : 0,
        assist: sum('assistPoints'),
        secondChance: sum('secondChancePoints'),
        offTurnovers: sum('pointsOffTurnovers'),
        fastbreak: tagged ? fastbreak : null,
        bench: benchPoints,
        freeThrows: totals.ftm
    }
}

// ---------- Advanced shot details (situation / play type / role / shot type) ----------
function shotDetails(teamLogs) {
    const shots = teamLogs.filter(isFieldGoal)
    const tagged = shots.filter(log => SHOT_DETAIL_GROUPS.some(group => log[group.key]))
    const groups = SHOT_DETAIL_GROUPS.map(group => {
        const withValue = shots.filter(log => log[group.key])
        const rows = group.options
            .map(option => {
                const items = withValue.filter(log => log[group.key] === option.value)
                const made = items.filter(isMade).length
                const points = items.reduce((sum, log) => sum + (isMade(log) ? shotValue(log) : 0), 0)
                const threes = items.filter(log => shotValue(log) === 3)
                const madeThrees = threes.filter(isMade).length
                return {
                    value: option.value,
                    label: option.label,
                    hint: option.hint,
                    definition: option.definition,
                    attempts: items.length,
                    made,
                    points,
                    share: pct(items.length, withValue.length),
                    fgPct: pct(made, items.length),
                    efg: pct(made + 0.5 * madeThrees, items.length),
                    pps: ratio(points, items.length)
                }
            })
            .filter(row => row.attempts > 0)
            .sort((a, b) => b.attempts - a.attempts)
        return { key: group.key, title: group.title, subtitle: group.subtitle, tagged: withValue.length, rows }
    })
    return { shots: shots.length, tagged: tagged.length, groups }
}

// ---------- On court: +/-, lineups, clutch ----------
function onCourtAnalysis(match, logs, playersById) {
    const teamIds = [String(match.team1.id), String(match.team2.id)]
    const onCourt = { [teamIds[0]]: new Set(), [teamIds[1]]: new Set() }
    const plusMinus = {}
    const lineups = { [teamIds[0]]: {}, [teamIds[1]]: {} }
    const lineupStart = { [teamIds[0]]: null, [teamIds[1]]: null }
    const score = { [teamIds[0]]: 0, [teamIds[1]]: 0 }
    const clutch = {
        [teamIds[0]]: { ...emptyTotals(), events: 0, scorers: {} },
        [teamIds[1]]: { ...emptyTotals(), events: 0, scorers: {} }
    }
    let lastPeriod = 1
    let plays = 0
    let playsTracked = 0

    const lineupKey = teamId => (onCourt[teamId].size === 5 ? [...onCourt[teamId]].sort().join('-') : null)
    const closeLineup = (teamId, at) => {
        const start = lineupStart[teamId]
        if (start && start.key) {
            const entry = lineups[teamId][start.key] || (lineups[teamId][start.key] = { seconds: 0, pf: 0, pa: 0 })
            entry.seconds += Math.max(0, at - start.at)
        }
        lineupStart[teamId] = { key: lineupKey(teamId), at }
    }

    for (const log of logs) {
        const teamId = String(log.teamId)
        if (!onCourt[teamId]) continue
        const at = elapsedSeconds(log)
        const period = periodOf(log, at)
        lastPeriod = Math.max(lastPeriod, period)
        if (log.playerMatchStatus === 'begin' || log.playerMatchStatus === 'stop') {
            closeLineup(teamId, at)
            if (log.playerMatchStatus === 'begin') onCourt[teamId].add(String(log.playerId))
            else onCourt[teamId].delete(String(log.playerId))
            lineupStart[teamId] = { key: lineupKey(teamId), at }
            continue
        }
        const opponentId = teamIds.find(id => id !== teamId)
        plays += 1
        if (onCourt[teamId].size === 5 && onCourt[opponentId].size === 5) playsTracked += 1

        // clutch: last 5 minutes of the 4th period or an overtime, score within 5
        const periodEnd = period <= 4 ? REGULATION_SECONDS : REGULATION_SECONDS + (period - 4) * OVERTIME_SECONDS
        const inClutch = period >= 4 && at >= periodEnd - CLUTCH_SECONDS && Math.abs(score[teamId] - score[opponentId]) <= CLUTCH_MARGIN
        if (inClutch) {
            const entry = clutch[teamId]
            addStats(entry, log)
            entry.events += 1
            const points = logPoints(log)
            if (points && log.playerId) entry.scorers[log.playerId] = (entry.scorers[log.playerId] || 0) + points
        }

        const points = logPoints(log)
        if (!points) continue
        score[teamId] += points
        onCourt[teamId].forEach(id => { plusMinus[id] = (plusMinus[id] || 0) + points })
        onCourt[opponentId].forEach(id => { plusMinus[id] = (plusMinus[id] || 0) - points })
        const own = lineupKey(teamId)
        const opposite = lineupKey(opponentId)
        if (own) {
            const entry = lineups[teamId][own] || (lineups[teamId][own] = { seconds: 0, pf: 0, pa: 0 })
            entry.pf += points
        }
        if (opposite) {
            const entry = lineups[opponentId][opposite] || (lineups[opponentId][opposite] = { seconds: 0, pf: 0, pa: 0 })
            entry.pa += points
        }
    }
    const gameEnd = REGULATION_SECONDS + Math.max(0, lastPeriod - 4) * OVERTIME_SECONDS
    teamIds.forEach(teamId => closeLineup(teamId, gameEnd))

    const lineupRows = teamId => Object.entries(lineups[teamId])
        .filter(([, entry]) => entry.seconds >= MIN_LINEUP_SECONDS)
        .map(([key, entry]) => ({
            key,
            players: key.split('-').map(id => playerShort(playersById[id])),
            seconds: entry.seconds,
            pf: entry.pf,
            pa: entry.pa,
            plusMinus: entry.pf - entry.pa
        }))
        .sort((a, b) => b.seconds - a.seconds)
        .slice(0, 5)

    const clutchOf = teamId => {
        const entry = withDerived(clutch[teamId])
        const top = Object.entries(entry.scorers).sort((a, b) => b[1] - a[1])[0]
        return { ...entry, topScorer: top ? { name: playerShort(playersById[top[0]]), points: top[1] } : null }
    }
    const clutchHappened = clutch[teamIds[0]].events + clutch[teamIds[1]].events > 0

    return {
        // substitutions logged with 5 on court for each team (nearly always): +/- and lineups can be trusted
        tracked: plays > 0 && playsTracked / plays >= MIN_TRACKED_SHARE && logs.some(log => log.playerMatchStatus === 'begin'),
        plusMinus,
        lineups: { team1: lineupRows(teamIds[0]), team2: lineupRows(teamIds[1]) },
        clutch: clutchHappened ? { team1: clutchOf(teamIds[0]), team2: clutchOf(teamIds[1]) } : null,
        gameSeconds: gameEnd
    }
}

// ---------- Assist network ----------
function assistNetwork(teamLogs, playersById) {
    const pairs = {}
    let lastBasket = null
    teamLogs.forEach(log => {
        if (isFieldGoal(log) && isMade(log)) {
            lastBasket = log
            return
        }
        if (num(log.assists) > 0 && log.playerId) {
            if (lastBasket && String(lastBasket.playerId) !== String(log.playerId)) {
                const key = `${log.playerId}>${lastBasket.playerId}`
                const entry = pairs[key] || (pairs[key] = { from: log.playerId, to: lastBasket.playerId, count: 0, points: 0 })
                entry.count += 1
                entry.points += shotValue(lastBasket)
            }
            lastBasket = null
        }
    })
    return Object.values(pairs)
        .map(pair => ({ ...pair, fromName: playerShort(playersById[pair.from]), toName: playerShort(playersById[pair.to]) }))
        .sort((a, b) => (b.count - a.count) || (b.points - a.points))
        .slice(0, 6)
}

// ---------- Players ----------
function playerRows(team, totals, plusMinus, tracked) {
    const players = (Array.isArray(team.players) ? team.players : []).filter(player => player.stats && player.stats[0])
    const teamMinutes = players.reduce((sum, player) => sum + num(player.stats[0].playTimeInSecs) / 60, 0)
    return players
        .map(player => {
            const s = player.stats[0]
            const minutes = num(s.playTimeInSecs) / 60
            const fga = num(s.twoPointAttempts) + num(s.threePointAttempts)
            const fgm = num(s.twoPointsMade) + num(s.threePointsMade)
            const fta = num(s.freeThrowAttempts)
            const tov = num(s.turnovers)
            const pts = num(s.points)
            const usage = minutes && teamMinutes && (totals.fga + 0.44 * totals.fta + totals.tov)
                ? (100 * (fga + 0.44 * fta + tov) * (teamMinutes / 5)) / (minutes * (totals.fga + 0.44 * totals.fta + totals.tov))
                : null
            const teamFgmWhileOn = minutes && teamMinutes ? (minutes / (teamMinutes / 5)) * totals.fgm - fgm : 0
            return {
                id: player.id,
                name: playerLabel(player),
                short: playerShort(player),
                jersey: player.alternativeJersey || player.alternativeJersey === 0 ? player.alternativeJersey : player.jersey,
                image: player.image,
                minutes,
                pts,
                plusMinus: tracked ? (plusMinus[String(player.id)] || 0) : null,
                ts: pct(pts, 2 * (fga + 0.44 * fta)),
                efg: pct(fgm + 0.5 * num(s.threePointsMade), fga),
                usage,
                astPct: teamFgmWhileOn > 0 ? pct(num(s.assists), teamFgmWhileOn) : null,
                // no turnovers: no ratio to show
                astTo: tov ? num(s.assists) / tov : null,
                ast: num(s.assists),
                tov
            }
        })
        .filter(row => row.minutes > 0 || row.pts > 0)
        .sort((a, b) => b.minutes - a.minutes)
}

// Everything the advanced analytics tab shows; `available` false when the match has nothing to analyse
export function buildAdvancedAnalytics(match, logs) {
    if (!match || !match.team1 || !match.team2) return { available: false }
    const ordered = orderedLogs(logs)
    const totals1 = teamTotals(match.team1, ordered)
    const totals2 = teamTotals(match.team2, ordered)
    if (totals1.fga + totals2.fga === 0) return { available: false }

    const playersById = {}
    ;[match.team1, match.team2].forEach(team => (team.players || []).forEach(player => { playersById[String(player.id)] = player }))
    const teamLogs = team => ordered.filter(log => String(log.teamId) === String(team.id) && !log.playerMatchStatus)
    const logs1 = teamLogs(match.team1)
    const logs2 = teamLogs(match.team2)

    const court = onCourtAnalysis(match, ordered, playersById)
    const gameMinutes = (ordered.length ? court.gameSeconds : REGULATION_SECONDS) / 60
    const profile1 = shotProfile(logs1)
    const profile2 = shotProfile(logs2)
    const details1 = shotDetails(logs1)
    const details2 = shotDetails(logs2)

    return {
        available: true,
        hasLogs: ordered.length > 0,
        totals: { team1: totals1, team2: totals2 },
        efficiency: efficiency(totals1, totals2, gameMinutes),
        profile: profile1.total + profile2.total > 0 ? { team1: profile1, team2: profile2 } : null,
        sources: {
            team1: pointSources(match.team1, totals1, logs1, ordered, profile1),
            team2: pointSources(match.team2, totals2, logs2, ordered, profile2)
        },
        details: details1.tagged + details2.tagged > 0 ? { team1: details1, team2: details2 } : null,
        players: {
            team1: playerRows(match.team1, totals1, court.plusMinus, court.tracked),
            team2: playerRows(match.team2, totals2, court.plusMinus, court.tracked)
        },
        plusMinusTracked: court.tracked,
        assists: ordered.length ? { team1: assistNetwork(logs1, playersById), team2: assistNetwork(logs2, playersById) } : null,
        lineups: court.tracked && (court.lineups.team1.length || court.lineups.team2.length) ? court.lineups : null,
        clutch: court.clutch
    }
}
